import { isNotNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import { Nullable } from '@clearverify/types';

import {
  UserStatus,
  VerifierInfoFragment,
  VerifierStaffRole,
  VerifierType,
  VerifierWhere,
} from 'src/api';
import { SelectFieldOption } from 'src/types/components';

type VerifiersTableFilter = {
  status?: Nullable<UserStatus[]>;
  type?: Nullable<SelectFieldOption<VerifierType>>;
};

export const getVerifierAdmins = (
  verifierStaffs: VerifierInfoFragment['verifierStaffsList'],
): VerifierInfoFragment['verifierStaffsList'] => {
  return verifierStaffs.filter(staffUser => staffUser.role === VerifierStaffRole.VerifierAdmin);
};

const determineVerifierAdminsStatus = (
  verifierAdmins: VerifierInfoFragment['verifierStaffsList'],
): UserStatus => {
  if (isEmpty(verifierAdmins)) {
    return UserStatus.Inactive;
  }

  if (verifierAdmins.some(verifierAdmin => verifierAdmin.user.status === UserStatus.Active)) {
    return UserStatus.Active;
  }

  if (verifierAdmins.every(verifierAdmin => verifierAdmin.user.status === UserStatus.Pending)) {
    return UserStatus.Pending;
  }

  return UserStatus.Inactive;
};

export const getVerifierStatus = (verifier: VerifierInfoFragment): UserStatus => {
  const verifierAdmins = getVerifierAdmins(verifier.verifierStaffsList);
  return determineVerifierAdminsStatus(verifierAdmins);
};

export const prepareVerifiersFilterByStatus = (
  filter: VerifiersTableFilter,
): VerifierWhere | undefined => {
  if (!isNotNil(filter.status) || isEmpty(filter.status)) return undefined;

  const hasActiveAdmin = {
    verifierStaffs: {
      some: {
        role: { equals: VerifierStaffRole.VerifierAdmin },
        user: { status: { equals: UserStatus.Active } },
      },
    },
  } satisfies VerifierWhere;

  const hasPendingAdmin = {
    verifierStaffs: {
      some: {
        role: { equals: VerifierStaffRole.VerifierAdmin },
        user: { status: { equals: UserStatus.Pending } },
      },
    },
  } satisfies VerifierWhere;

  const hasInactiveAdmin = {
    verifierStaffs: {
      some: {
        role: { equals: VerifierStaffRole.VerifierAdmin },
        user: { status: { equals: UserStatus.Inactive } },
      },
    },
  } satisfies VerifierWhere;

  const hasAnyAdmin = {
    verifierStaffs: {
      some: {
        role: { equals: VerifierStaffRole.VerifierAdmin },
      },
    },
  } satisfies VerifierWhere;

  return {
    OR: filter.status.map((status): VerifierWhere => {
      // Active: at least one Admin with Active status
      if (status === UserStatus.Active) {
        return hasActiveAdmin;
      }

      // Pending: no Active admins, all admins are Pending (at least one exists)
      if (status === UserStatus.Pending) {
        return {
          AND: [{ NOT: [hasActiveAdmin] }, hasPendingAdmin, { NOT: [hasInactiveAdmin] }],
        };
      }

      // Inactive: no Active admins AND (no admins at all OR at least one Inactive admin)
      return {
        AND: [
          { NOT: [hasActiveAdmin] },
          {
            OR: [{ NOT: [hasAnyAdmin] }, hasInactiveAdmin],
          },
        ],
      };
    }),
  };
};

export const prepareVerifiersFilter = (filter: VerifiersTableFilter): VerifierWhere | undefined => {
  if (isEmpty(filter)) return undefined;

  const typeFilter: VerifierWhere | undefined = filter.type?.value
    ? {
        type: {
          equals: filter.type.value,
        },
      }
    : undefined;

  return {
    AND: [typeFilter, prepareVerifiersFilterByStatus(filter)].filter(isNotNil),
  };
};
